/**
 * What the character IS, on the sheet: Tables 2.5 and 2.6, and the prose.
 *
 * ⚠ **None of this had anywhere to live until 2026-08-30.** The sheet recorded
 * a name, a race, a class and an alignment, so the Dungeon Master was told
 * nothing about the body it was describing and invented one per beat — a human
 * mage was given fur and antennae, and her sex changed between consecutive
 * beats of one scene. `races.json → heightWeight` and `→ aging` had been
 * extracted since the first pass and read by nothing.
 *
 * Two of the four are **editable and two are not**, and the split is not
 * arbitrary. Gender and the description are authorship: they buy nothing, gate
 * nothing, and are read only by the DM's prompt, so the player owning them is
 * the point. **Age is refused by the server by name**, because its ability
 * adjustment was applied to the scores when the character was made and nothing
 * on the row now records what it cost — accepting a new one would either
 * contradict the scores beside it or apply a second adjustment on top of the
 * first. Height and weight are shown as rolled; nothing in play changes them.
 *
 * ⚠ **Never regenerated.** The DM writes a description once, at creation, when
 * the player leaves it blank. A model is not deterministic, so a second run
 * writes a *different* character — the same reasoning that makes
 * `world_state.worldSeeded` refuse a second world. Only a human writes one
 * after the first.
 */

import { useEffect, useState } from 'react';

// **Shared, not a local copy.** `sceneFacts` renders the same height for the
// model, and this project has already paid for a byte-identical definition
// living in two files — see `InventoryItem`.
import { feetAndInches } from '@portal/shared';

export function AppearanceSection({
  character,
  readOnly,
  onChange,
}: {
  readonly character: {
    gender?: string | null;
    age?: number | null;
    heightIn?: number | null;
    weightLb?: number | null;
    description?: string | null;
  };
  readonly readOnly: boolean;
  readonly onChange: (patch: { gender?: string; description?: string }) => void;
}) {
  const [description, setDescription] = useState(character.description ?? '');
  const [gender, setGender] = useState(character.gender ?? '');
  // Whether anything has been typed since this opened. A "saved" cue is only honest after an edit, and only
  // once the sheet that came back agrees with the box: the server's answer decides, not the keypress.
  const [touched, setTouched] = useState(false);

  // The write returns the server's answer and the sheet is refetched, so the
  // boxes have to follow what came back rather than keeping what was typed —
  // otherwise a refused edit stays on screen looking saved.
  useEffect(() => setDescription(character.description ?? ''), [character.description]);
  useEffect(() => setGender(character.gender ?? ''), [character.gender]);

  const facts: [string, string][] = [
    ...(character.age !== null && character.age !== undefined
      ? ([['Age', `${character.age}`]] as [string, string][])
      : []),
    ...(character.heightIn !== null && character.heightIn !== undefined
      ? ([['Height', feetAndInches(character.heightIn)]] as [string, string][])
      : []),
    ...(character.weightLb !== null && character.weightLb !== undefined
      ? ([['Weight', `${character.weightLb} lb`]] as [string, string][])
      : []),
  ];

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Appearance
      </h2>

      {facts.length > 0 && (
        <ul className="flex flex-wrap gap-4 text-sm tabular-nums">
          {facts.map(([label, value]) => (
            <li key={label}>
              <span className="text-neutral-500 dark:text-neutral-400">{label} </span>
              {value}
            </li>
          ))}
        </ul>
      )}

      {readOnly ? (
        <>
          {gender && <p className="text-sm">{gender}</p>}
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {description || 'nothing recorded'}
          </p>
        </>
      ) : (
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="block text-xs text-neutral-500 dark:text-neutral-400">Gender</span>
            <input
              value={gender}
              aria-label="Gender"
              maxLength={32}
              onChange={(e) => {
                setGender(e.target.value);
                setTouched(true);
              }}
              onBlur={() => gender !== (character.gender ?? '') && onChange({ gender })}
              className="w-48 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
            />
          </label>
          <label className="block space-y-1">
            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
              What they look like. This is yours to edit, and it saves when you click away from the box. The
              Dungeon Master reads it on every beat but never changes it.
            </span>
            <textarea
              value={description}
              aria-label="Appearance"
              rows={6}
              maxLength={400}
              onChange={(e) => {
                setDescription(e.target.value);
                setTouched(true);
              }}
              // Saved on blur rather than per keystroke: the write re-reads the
              // whole sheet, and a PATCH per character typed would be a request
              // per letter of a 400-character field.
              onBlur={() =>
                description !== (character.description ?? '') && onChange({ description })
              }
              className="w-full max-w-lg rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
            />
          </label>
          {touched && (
            <p role="status" className="text-xs text-neutral-500 dark:text-neutral-400">
              {description === (character.description ?? '') && gender === (character.gender ?? '')
                ? 'Saved.'
                : 'Not saved yet — click outside the box to save.'}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
